import { useEffect } from "react"
import type { Editor } from "@tiptap/react"

import type { DiagramEditRequest } from "../types"
import { isDiagramContentType, DIAGRAM_DATA_ATTR } from "../utils/diagramConstants"

const findDiagramNodePos = (editor: Editor, target: HTMLElement): number | null => {
  const coords = { left: 0, top: 0 }
  const rect = target.getBoundingClientRect()
  coords.left = rect.left + rect.width / 2
  coords.top = rect.top + rect.height / 2

  const posAtCoords = editor.view.posAtCoords(coords)
  if (!posAtCoords) return null

  const $pos = editor.state.doc.resolve(posAtCoords.pos)
  for (let depth = $pos.depth; depth >= 0; depth -= 1) {
    const node = $pos.node(depth)
    if (node.type.name === "diagram") {
      return $pos.before(depth)
    }
  }

  const direct = editor.state.doc.nodeAt(posAtCoords.pos)
  if (direct?.type.name === "diagram") {
    return posAtCoords.pos
  }

  const before = editor.state.doc.nodeAt(posAtCoords.pos - 1)
  if (before?.type.name === "diagram") {
    return posAtCoords.pos - 1
  }

  return null
}

export const useDiagramClickEdit = (
  editor: Editor | null,
  editable: boolean,
  onEditRequest?: (request: DiagramEditRequest) => void
) => {
  useEffect(() => {
    if (!editor || !editable || !onEditRequest) return

    const handleClick = (event: MouseEvent) => {
      const target = event.target
      if (!(target instanceof HTMLImageElement)) return

      const contentType = target.getAttribute(DIAGRAM_DATA_ATTR.contentType)
      if (!isDiagramContentType(contentType)) return

      const pos = findDiagramNodePos(editor, target)
      if (pos == null) return

      const node = editor.state.doc.nodeAt(pos)
      if (!node || node.type.name !== "diagram") return

      event.preventDefault()
      onEditRequest({
        pos,
        src: node.attrs.src ?? target.src,
        diagramXml: node.attrs.diagramXml ?? "",
        contentType,
      })
    }

    const root = editor.view.dom
    root.addEventListener("click", handleClick)
    return () => root.removeEventListener("click", handleClick)
  }, [editor, editable, onEditRequest])
}
